import api from "@/api";
import converters from "@/converters";
import type { ProfileFragment } from "@/graphql";
import { Profile_Type } from "@/graphql/generated-types";
import isDataReady from "@/helpers/requests/isDataReady";
import type { IMultiSelectItem, IOption } from "@/interfaces";
import { sentryException } from "@/sentry";
import type { LoadingType } from "@/types";
import { useAuthStore } from "@/stores/authStore";
import { computed, ref } from "vue";

const init = ref<boolean>(false);
const profiles = ref<ProfileFragment[]>([]);
const status = ref<LoadingType>("none");

export function useProfiles() {
  const authStore = useAuthStore();
  const load = async () => {
    if (status.value === "loaded") {
      return;
    }

    try {
      status.value = "loading";
      profiles.value = await api.profile.getProfiles();
    } catch (error) {
      sentryException(error);
    } finally {
      status.value = "loaded";
    }
  };

  const residents = computed(() => {
    return profiles.value.filter(
      (profile) => profile.type === Profile_Type.Resident,
    );
  });

  const employees = computed(() => {
    return profiles.value.filter(
      (profile) => profile.type === Profile_Type.Employee,
    );
  });

  const employeeList = computed<IMultiSelectItem[]>(() =>
    employees.value
      .map(converters.profiles.toMultiSelectItem)
      .toSorted((a, b) =>
        a.title.localeCompare(b.title, authStore.institutionLocale),
      ),
  );

  const residentList = computed<IMultiSelectItem[]>(() =>
    residents.value
      .map(converters.profiles.toMultiSelectItem)
      .toSorted((a, b) =>
        a.title.localeCompare(b.title, authStore.institutionLocale),
      ),
  );

  const employeeOptions = computed<IOption[]>(() =>
    employees.value.map(converters.profiles.toOption),
  );

  const residentOptions = computed<IOption[]>(() =>
    residents.value.map(converters.profiles.toOption),
  );

  const loading = computed(() => {
    return status.value === "loading";
  });

  const isReady = () => isDataReady(status);

  // We don't need to fetch over and over again. We should only fetch one time.
  if (!init.value) {
    init.value = true;
    load();
  }

  return {
    loading,
    residents,
    employees,
    employeeList,
    residentList,
    employeeOptions,
    residentOptions,
    isReady,
  };
}
